import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  Upload,
  Database,
  CheckCircle,
  AlertTriangle,
  Clock,
  FileText,
  Calculator,
  TrendingUp,
  RefreshCw,
  Download,
  Eye,
  Sparkles,
  HelpCircle
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell
} from 'recharts';
import AIAssistantModal from '../components/AIAssistantModal';

const DataLab = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pipeline');
  const [emissions, setEmissions] = useState(null);
  const [uploads, setUploads] = useState([]);
  
  // AI Anomaly Explanation State
  const [showAnomalyModal, setShowAnomalyModal] = useState(false);
  const [anomalyLoading, setAnomalyLoading] = useState(false);
  const [anomalyResult, setAnomalyResult] = useState(null);
  const [anomalyError, setAnomalyError] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState(null);

  useEffect(() => {
    fetchData();
    logAccess();
  }, []);

  const logAccess = async () => {
    // Silently ignore audit logging errors (apiFetch never throws for HTTP errors)
    await apiFetch(`${API}/audit/log-access`, {
      method: 'POST',
      body: {
        dashboard_id: 'analyst',
        dashboard_name: 'Data Lab',
        action: 'dashboard_view',
        timestamp: new Date().toISOString()
      }
    });
  };

  const fetchData = async () => {
    const uploadsData = await apiFetch(`${API}/data/files`);
    setUploads(uploadsData?._error ? [] : (uploadsData?.files || []));
    setLoading(false);
  };

  // Data quality metrics
  const qualityMetrics = [
    { source: 'Emissions Data', quality: 94, records: 1250, issues: 12, lastUpdated: '2 hours ago' },
    { source: 'Workforce Data', quality: 98, records: 2475, issues: 3, lastUpdated: '1 day ago' },
    { source: 'Governance Data', quality: 91, records: 89, issues: 8, lastUpdated: '3 days ago' },
    { source: 'Supply Chain', quality: 76, records: 340, issues: 45, lastUpdated: '1 week ago' }
  ];

  // Emissions calculation data
  const emissionsData = [
    { month: 'Jul', scope1: 4520, scope2: 3200, scope3: 12500 },
    { month: 'Aug', scope1: 4380, scope2: 3100, scope3: 12200 },
    { month: 'Sep', scope1: 4250, scope2: 2980, scope3: 11800 },
    { month: 'Oct', scope1: 4100, scope2: 2850, scope3: 11500 },
    { month: 'Nov', scope1: 3980, scope2: 2720, scope3: 11200 },
    { month: 'Dec', scope1: 3850, scope2: 2600, scope3: 10900 }
  ];

  // Methodology library
  const methodologies = [
    { id: 'ghg-protocol', name: 'GHG Protocol Corporate Standard', version: '2015 Revised', status: 'active' },
    { id: 'esrs-e1', name: 'ESRS E1 Climate Change', version: 'EFRAG Set 1 2023', status: 'active' },
    { id: 'iso-14064', name: 'ISO 14064-1', version: '2018', status: 'active' },
    { id: 'pcaf', name: 'PCAF Financed Emissions', version: '2.0', status: 'optional' }
  ];

  // Explain Anomaly with AI
  const explainAnomaly = async (metric) => {
    setSelectedMetric(metric);
    setShowAnomalyModal(true);
    setAnomalyLoading(true);
    setAnomalyError(null);
    setAnomalyResult(null);
    
    const data = await apiFetch(`${API}/ai/analyst/explain-anomaly`, {
      method: 'POST',
      body: {
        data_source: metric.source,
        metric_name: metric.source,
        current_value: metric.quality,
        expected_range: { min: 85, max: 100 },
        historical_values: [92, 94, 91, 93, metric.quality]
      }
    });
    if (data?._error) {
      setAnomalyError(data.message);
    } else {
      setAnomalyResult(data);
    }
    setAnomalyLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 rounded-lg bg-purple-600 flex items-center justify-center animate-pulse">
          <BarChart3 size={24} className="text-white" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="data-lab">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/dashboard-selector')} className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft size={20} className="text-slate-600" />
          </button>
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Data Lab
            </h1>
            <p className="text-slate-600">Granular data analysis and methodology documentation</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link
            to="/data-upload"
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            <Upload size={16} />
            Upload Data
          </Link>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Database size={20} className="text-purple-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Data Sources</p>
              <p className="text-2xl font-bold text-slate-900">4</p>
            </div>
          </div>
          <p className="text-xs text-emerald-600">All connected</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <CheckCircle size={20} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Avg Quality</p>
              <p className="text-2xl font-bold text-slate-900">90%</p>
            </div>
          </div>
          <p className="text-xs text-emerald-600">+2% from last month</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <AlertTriangle size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Data Issues</p>
              <p className="text-2xl font-bold text-slate-900">68</p>
            </div>
          </div>
          <p className="text-xs text-amber-600">45 in supply chain</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <Clock size={20} className="text-blue-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase">Last Refresh</p>
              <p className="text-2xl font-bold text-slate-900">2h</p>
            </div>
          </div>
          <p className="text-xs text-blue-600">Auto-sync enabled</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {[
          { id: 'pipeline', label: 'Data Pipeline', icon: Database },
          { id: 'emissions', label: 'Emissions Calculator', icon: Calculator },
          { id: 'quality', label: 'Quality Scores', icon: CheckCircle },
          { id: 'methodology', label: 'Methodology Docs', icon: FileText }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id
                ? 'text-purple-600 border-purple-600'
                : 'text-slate-500 border-transparent hover:text-slate-700'
            }`}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Pipeline Tab */}
      {activeTab === 'pipeline' && (
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Data Ingestion Status</h3>
            <div className="space-y-4">
              {qualityMetrics.map((source, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${source.quality >= 90 ? 'bg-emerald-500' : source.quality >= 80 ? 'bg-amber-500' : 'bg-red-500'}`} />
                    <div>
                      <p className="text-sm font-medium text-slate-900">{source.source}</p>
                      <p className="text-xs text-slate-500">{source.records.toLocaleString()} records</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-slate-900">{source.quality}%</p>
                    <p className="text-xs text-slate-500">{source.lastUpdated}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Recent Uploads</h3>
            {uploads.length > 0 ? (
              <div className="space-y-3">
                {uploads.slice(0, 5).map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText size={20} className="text-slate-400" />
                      <div>
                        <p className="text-sm font-medium text-slate-900">{file.filename}</p>
                        <p className="text-xs text-slate-500">{file.row_count} rows</p>
                      </div>
                    </div>
                    <span className="px-2 py-1 bg-emerald-100 text-emerald-700 text-xs rounded">Processed</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Upload size={40} className="text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No files uploaded yet</p>
                <Link to="/data-upload" className="text-purple-600 text-sm hover:underline">Upload your first file</Link>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Emissions Calculator Tab */}
      {activeTab === 'emissions' && (
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-semibold text-slate-900 mb-4">GHG Emissions Trend (Scope 1, 2, 3)</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={emissionsData}>
                  <defs>
                    <linearGradient id="scope1" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#059669" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#059669" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="scope2" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="scope3" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" stroke="#94A3B8" fontSize={12} />
                  <YAxis stroke="#94A3B8" fontSize={12} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }} />
                  <Area type="monotone" dataKey="scope1" stroke="#059669" strokeWidth={2} fill="url(#scope1)" name="Scope 1" />
                  <Area type="monotone" dataKey="scope2" stroke="#3B82F6" strokeWidth={2} fill="url(#scope2)" name="Scope 2" />
                  <Area type="monotone" dataKey="scope3" stroke="#8B5CF6" strokeWidth={2} fill="url(#scope3)" name="Scope 3" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex justify-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span className="text-sm text-slate-600">Scope 1 (Direct)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-sm text-slate-600">Scope 2 (Energy)</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-purple-500" />
                <span className="text-sm text-slate-600">Scope 3 (Value Chain)</span>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Emission Factor Library</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 border border-slate-200 rounded-lg">
                <p className="text-sm font-medium text-slate-900">Electricity (Grid)</p>
                <p className="text-2xl font-bold text-purple-600">0.385</p>
                <p className="text-xs text-slate-500">kg CO2e/kWh (DE 2024)</p>
              </div>
              <div className="p-4 border border-slate-200 rounded-lg">
                <p className="text-sm font-medium text-slate-900">Natural Gas</p>
                <p className="text-2xl font-bold text-purple-600">2.02</p>
                <p className="text-xs text-slate-500">kg CO2e/m³</p>
              </div>
              <div className="p-4 border border-slate-200 rounded-lg">
                <p className="text-sm font-medium text-slate-900">Diesel</p>
                <p className="text-2xl font-bold text-purple-600">2.68</p>
                <p className="text-xs text-slate-500">kg CO2e/liter</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quality Scores Tab */}
      {activeTab === 'quality' && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900">Data Quality Assessment</h3>
          </div>
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Source</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Quality Score</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Records</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Issues</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Last Updated</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {qualityMetrics.map((metric, idx) => (
                <tr key={idx} className="border-t border-slate-100">
                  <td className="px-4 py-3 text-sm font-medium text-slate-900">{metric.source}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full ${metric.quality >= 90 ? 'bg-emerald-500' : metric.quality >= 80 ? 'bg-amber-500' : 'bg-red-500'}`}
                          style={{ width: `${metric.quality}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium">{metric.quality}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{metric.records.toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${metric.issues > 20 ? 'bg-red-100 text-red-700' : metric.issues > 5 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {metric.issues} issues
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500">{metric.lastUpdated}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {metric.issues > 5 && (
                        <button 
                          onClick={() => explainAnomaly(metric)}
                          className="text-purple-600 hover:text-purple-800 text-sm font-medium flex items-center gap-1"
                          data-testid={`explain-anomaly-${metric.source.replace(/\s+/g, '-')}`}
                        >
                          <Sparkles size={14} /> Explain
                        </button>
                      )}
                      <button className="text-purple-600 hover:text-purple-800 text-sm font-medium flex items-center gap-1">
                        <Eye size={14} /> Review
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Methodology Tab */}
      {activeTab === 'methodology' && (
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-semibold text-slate-900 mb-4">Calculation Methodology Library</h3>
          <div className="space-y-4">
            {methodologies.map((method) => (
              <div key={method.id} className="p-4 border border-slate-200 rounded-lg flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-medium text-slate-900">{method.name}</h4>
                    <span className={`px-2 py-0.5 text-xs rounded ${method.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
                      {method.status}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">Version: {method.version}</p>
                </div>
                <div className="flex gap-2">
                  <button className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm hover:bg-slate-50 flex items-center gap-1">
                    <Eye size={14} /> View
                  </button>
                  <button className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm hover:bg-slate-50 flex items-center gap-1">
                    <Download size={14} /> Export
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Anomaly Explanation Modal */}
      <AIAssistantModal
        isOpen={showAnomalyModal}
        onClose={() => setShowAnomalyModal(false)}
        title={`Explain Anomaly: ${selectedMetric?.source || ''}`}
        icon={HelpCircle}
        iconColor="text-purple-600"
        iconBg="bg-purple-100"
        loading={anomalyLoading}
        result={anomalyResult}
        error={anomalyError}
        onRetry={() => selectedMetric && explainAnomaly(selectedMetric)}
      />
    </div>
  );
};

export default DataLab;
